import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import ConfirmDialog from '../components/ConfirmDialog';
import { apiService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import './AmazonSQS.css';

interface QueueItem {
  queueName: string;
  queueUrl: string;
  queueArn: string;
  environment?: string;
  featureName: string;
  description: string;
  pollingEnabled: boolean;
  state: string; // 'Enabled', 'Disabled', 'Enabling', 'Disabling', 'NoTrigger'
  eventSourceMappingUuid?: string;
  functionArn?: string;
  functionName?: string;
  approximateNumberOfMessages: string;
  approximateNumberOfMessagesNotVisible: string;
  updatedAt?: string;
}

const AmazonSQS: React.FC = () => {
  const { showSuccess, showError } = useNotification();
  const [queues, setQueues] = useState<QueueItem[]>([]);
  const [environment, setEnvironment] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [togglingQueue, setTogglingQueue] = useState<string | null>(null);
  const [envFilter, setEnvFilter] = useState<'all' | 'dev' | 'staging'>('all');

  // Confirmation modal state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    queue: QueueItem | null;
    targetEnabled: boolean;
  }>({
    open: false,
    queue: null,
    targetEnabled: false,
  });

  const fetchQueues = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.listSqsQueues();
      const data = res.data;
      setQueues(data.queues || []);
      setEnvironment(data.environment || 'staging');
    } catch (err: any) {
      console.error('Failed to load SQS queues:', err);
      showError(err.message || 'Failed to fetch SQS queues');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchQueues();
  }, [fetchQueues]);

  const getStatusPill = (queue: QueueItem) => {
    if (queue.state === 'Enabling') {
      return (
        <span className="status-pill pill-transitioning">
          <span className="status-dot status-dot-pulse"></span>
          Enabling in AWS...
        </span>
      );
    }
    if (queue.state === 'Disabling') {
      return (
        <span className="status-pill pill-transitioning">
          <span className="status-dot status-dot-pulse"></span>
          Disabling in AWS...
        </span>
      );
    }
    if (queue.pollingEnabled) {
      return (
        <span className="status-pill pill-active">
          <span className="status-dot"></span>
          Polling Enabled
        </span>
      );
    }
    return (
      <span className="status-pill pill-disabled">
        <span className="status-dot"></span>
        Polling Disabled
      </span>
    );
  };

  const requestToggle = (queue: QueueItem) => {
    const target = !queue.pollingEnabled;
    setConfirmDialog({
      open: true,
      queue,
      targetEnabled: target,
    });
  };

  const handleConfirmToggle = async () => {
    const { queue, targetEnabled } = confirmDialog;
    if (!queue) return;

    setConfirmDialog({ open: false, queue: null, targetEnabled: false });
    setTogglingQueue(queue.queueName);

    try {
      await apiService.toggleSqsQueue({
        queueName: queue.queueName,
        enabled: targetEnabled,
        uuid: queue.eventSourceMappingUuid,
      });

      showSuccess(
        `SQS Queue "${queue.queueName}" polling is now ${
          targetEnabled ? 'ENABLED' : 'DISABLED'
        }`
      );

      // Refresh list to show updated state
      await fetchQueues();
    } catch (err: any) {
      console.error('Failed to toggle queue polling:', err);
      showError(err.message || 'Failed to toggle queue polling');
    } finally {
      setTogglingQueue(null);
    }
  };

  const activeCount = queues.filter((q) => q.pollingEnabled).length;
  const pausedCount = queues.filter((q) => !q.pollingEnabled).length;

  const devCount = queues.filter((q) => (q.environment || '').toLowerCase() === 'dev').length;
  const stagingCount = queues.filter((q) => (q.environment || '').toLowerCase() === 'staging').length;
  const hasMultipleEnvs = devCount > 0 && stagingCount > 0;

  const filteredQueues = queues.filter((q) => {
    if (envFilter === 'all') return true;
    return (q.environment || '').toLowerCase() === envFilter;
  });

  return (
    <Layout>
      <div className="sqs-page">
        {/* Top Header */}
        <div className="sqs-header">
          <div>
            <div className="sqs-title-row">
              <h1>Amazon SQS Management</h1>
              <span className={`sqs-env-badge env-${environment}`}>
                {environment === 'dev-staging' ? 'DEV & STAGING' : environment.toUpperCase() || 'ENV'}
              </span>
            </div>
            <p className="sqs-subtitle">
              {environment === 'dev-staging'
                ? 'Manage AWS Lambda polling triggers for both Dev and Staging SQS queues in this shared environment.'
                : 'Manage AWS Lambda polling event source triggers for SQS queues in this environment.'}
              {' '}Disabling polling immediately pauses worker consumption while safely retaining messages.
            </p>
          </div>

          <div className="sqs-actions">
            <button
              className="sqs-refresh-btn"
              onClick={fetchQueues}
              disabled={loading}
              title="Refresh Queue Status"
            >
              {loading ? 'Refreshing...' : '↻ Refresh'}
            </button>
          </div>
        </div>

        {/* Status Metrics */}
        <div className="sqs-metrics-grid">
          <div className="metric-card">
            <span className="metric-label">Total Queues</span>
            <span className="metric-value">{queues.length}</span>
            {hasMultipleEnvs && (
              <span className="metric-subtext font-mono">
                {devCount} Dev · {stagingCount} Staging
              </span>
            )}
          </div>
          <div className="metric-card metric-active">
            <span className="metric-label">Polling Active</span>
            <span className="metric-value">{activeCount}</span>
          </div>
          <div className="metric-card metric-paused">
            <span className="metric-label">Polling Disabled / Paused</span>
            <span className="metric-value">{pausedCount}</span>
          </div>
        </div>

        {/* Environment Filter Tabs (shown when multiple environments exist) */}
        {hasMultipleEnvs && (
          <div className="sqs-filter-bar">
            <button
              className={`sqs-filter-btn ${envFilter === 'all' ? 'active' : ''}`}
              onClick={() => setEnvFilter('all')}
            >
              All Queues <span className="sqs-filter-count">{queues.length}</span>
            </button>
            <button
              className={`sqs-filter-btn ${envFilter === 'dev' ? 'active' : ''}`}
              onClick={() => setEnvFilter('dev')}
            >
              Dev <span className="sqs-filter-count">{devCount}</span>
            </button>
            <button
              className={`sqs-filter-btn ${envFilter === 'staging' ? 'active' : ''}`}
              onClick={() => setEnvFilter('staging')}
            >
              Staging <span className="sqs-filter-count">{stagingCount}</span>
            </button>
          </div>
        )}

        {/* Queues List */}
        {loading && queues.length === 0 ? (
          <div className="sqs-loading-state">
            <div className="sqs-spinner"></div>
            <p>Scanning environment SQS queues and Lambda triggers...</p>
          </div>
        ) : filteredQueues.length === 0 ? (
          <div className="sqs-empty-state">
            <h3>No Queues Found</h3>
            <p>No SQS queues match the selected filter.</p>
            <button className="sqs-retry-btn" onClick={fetchQueues}>
              Retry Scan
            </button>
          </div>
        ) : (
          <div className="sqs-cards-list">
            {filteredQueues.map((queue) => {
              const isToggling = togglingQueue === queue.queueName;
              return (
                <div
                  key={queue.queueName}
                  className={`sqs-card ${
                    queue.pollingEnabled ? 'card-enabled' : 'card-disabled'
                  }`}
                >
                  <div className="sqs-card-main">
                    <div className="sqs-card-header">
                      <div className="sqs-card-title-group">
                        {queue.environment && (
                          <span className={`queue-env-pill pill-${queue.environment}`}>
                            {queue.environment.toUpperCase()}
                          </span>
                        )}
                        <span className="feature-chip">{queue.featureName}</span>
                        <h3 className="queue-name">{queue.queueName}</h3>
                      </div>

                      <div className="sqs-status-indicator">
                        {getStatusPill(queue)}
                      </div>
                    </div>

                    <p className="queue-description">{queue.description}</p>

                    <div className="queue-meta-grid">
                      <div className="meta-item">
                        <span className="meta-label">Target Lambda Worker</span>
                        <span className="meta-value font-mono">
                          {queue.functionName || 'Direct Queue (No Trigger)'}
                        </span>
                      </div>
                      <div className="meta-item">
                        <span className="meta-label">Approx. Messages</span>
                        <span className="meta-value">
                          {queue.approximateNumberOfMessages} available
                          {queue.approximateNumberOfMessagesNotVisible !== '0' &&
                            ` (${queue.approximateNumberOfMessagesNotVisible} in-flight)`}
                        </span>
                      </div>
                      <div className="meta-item">
                        <span className="meta-label">Trigger State</span>
                        <span className="meta-value font-mono">{queue.state}</span>
                      </div>
                    </div>
                  </div>

                  {/* Toggle Control */}
                  <div className="sqs-card-actions">
                    {queue.state === 'Enabling' || queue.state === 'Disabling' ? (
                      <span className="transitioning-text">transitioning ...</span>
                    ) : (
                      <div className="toggle-container">
                        <span className="toggle-label">
                          {queue.pollingEnabled ? 'Active' : 'Disabled'}
                        </span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={queue.pollingEnabled}
                          disabled={isToggling}
                          className={`switch-button ${
                            queue.pollingEnabled ? 'switch-on' : 'switch-off'
                          } ${isToggling ? 'switch-busy' : ''}`}
                          onClick={() => requestToggle(queue)}
                          title={
                            queue.pollingEnabled
                              ? 'Click to pause/disable polling'
                              : 'Click to resume/enable polling'
                          }
                        >
                          <span className="switch-slider"></span>
                        </button>
                      </div>
                    )}

                    {isToggling && (
                      <span className="updating-indicator">Updating trigger...</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Confirmation Dialog */}
        {confirmDialog.open && confirmDialog.queue && (
          <ConfirmDialog
            isOpen={confirmDialog.open}
            title={
              confirmDialog.targetEnabled
                ? 'Resume SQS Polling?'
                : 'Pause / Disable SQS Polling?'
            }
            message={
              confirmDialog.targetEnabled
                ? `Enabling polling for "${confirmDialog.queue.queueName}" will resume worker processing for this feature. Messages in the queue will be consumed.`
                : `Disabling polling for "${confirmDialog.queue.queueName}" will immediately STOP the Lambda worker from pulling messages. Messages will remain safely stored in SQS, but this feature will not process incoming data until re-enabled.`
            }
            confirmText={confirmDialog.targetEnabled ? 'Enable Polling' : 'Disable Polling'}
            cancelText="Cancel"
            type={confirmDialog.targetEnabled ? 'info' : 'danger'}
            onConfirm={handleConfirmToggle}
            onCancel={() =>
              setConfirmDialog({ open: false, queue: null, targetEnabled: false })
            }
          />
        )}
      </div>
    </Layout>
  );
};

export default AmazonSQS;
